import React, { useCallback, useState } from 'react';
import { Box, Caption, NumericInput, Stack, Textarea } from '@gafe/common-ui';
import { kebabCase } from 'lodash';

import { useOrderContext, useOrderDispatch } from '@/components/modals/SubmitOrder/helpers';

import { SectionToggler } from '../../../components/SectionToggler';

import { ActionIcon } from './components/ActionIcon';
import { accessAddonInfo } from './constants';
import { Props } from './types';

import styles from './AddonRow.module.scss';

export const AddonRow = ({ type }: Props) => {
  const state = useOrderContext();
  const dispatch = useOrderDispatch();
  const [value, setValue] = useState<string | undefined>();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [textAreaValue, setTextAreaValue] = useState<string>('');
  const { label, description } = accessAddonInfo[type];
  const sectionData = state.order?.projectOptions?.[type];

  const onToggle = useCallback(
    (toggle: boolean) => {
      const payload = toggle
        ? {
            price: Number(value) > 0 ? Number(value) : null,
            notes: textAreaValue.trim(),
          }
        : null;

      dispatch({
        type: 'UPDATE_PROJECT_OPTIONS',
        payload: {
          [type]: payload,
        },
      });
    },
    [dispatch, textAreaValue, type, value],
  );

  const onEditClick = useCallback(() => {
    setEditMode(true);
  }, []);

  const onDeleteClick = useCallback(() => {
    dispatch({
      type: 'UPDATE_PROJECT_OPTIONS',
      payload: {
        [type]: {
          price: sectionData?.price,
          notes: null,
        },
      },
    });

    setTextAreaValue('');
  }, [dispatch, sectionData?.price, type]);

  const onSaveClick = useCallback(() => {
    setEditMode(false);

    dispatch({
      type: 'UPDATE_PROJECT_OPTIONS',
      payload: {
        [type]: {
          price: value,
          notes: textAreaValue.trim(),
        },
      },
    });
  }, [dispatch, type, value, textAreaValue]);

  const onCancelClick = useCallback(() => {
    setEditMode(false);
  }, []);

  const onNoteInputChange = useCallback((value: string) => {
    setTextAreaValue(value);
  }, []);

  const onAmountChange = useCallback(
    (value: string | undefined) => {
      setValue(value);
      dispatch({
        type: 'UPDATE_PROJECT_OPTIONS',
        payload: {
          [type]: {
            price: Number(value) > 0 ? Number(value) : null,
            notes: sectionData?.notes,
          },
        },
      });
    },
    [dispatch, sectionData?.notes, type],
  );

  const shouldShowSavedNote = !editMode && sectionData?.notes;
  const shouldShowAddNoteButton = !sectionData?.notes && !editMode;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>{label}</span>
        <section className={styles.toggler_wrapper}>
          <SectionToggler
            enabled={sectionData === undefined ? null : Boolean(sectionData)}
            name={label}
            onToggle={onToggle}
          />
        </section>
      </div>

      {Boolean(sectionData) && (
        <Stack gap="sm" orientation="vertical">
          <Caption colorSchema="dark">{description}</Caption>

          <section className={styles.amount}>
            <NumericInput
              allowLeadingZeros={false}
              allowNegative={false}
              data-testid={kebabCase(`${label}-amount`)}
              decimalScale={2}
              fixedDecimalScale
              onChange={onAmountChange}
              placeholder="$ -"
              prefix="$"
              thousandSeparator=","
              value={value ? value : ''}
              width="lg"
            />
          </section>

          <Stack gap="sm" orientation="vertical">
            {shouldShowSavedNote && (
              <Box
                border={{ style: 'none', width: 'md', radius: '2xs' }}
                colorSchema="neutral-light"
                padding={{ x: 'xs', y: '2xs' }}
              >
                <Caption>{sectionData.notes}</Caption>
              </Box>
            )}

            {shouldShowAddNoteButton && (
              <div className={styles.actions_wrapper}>
                <ActionIcon onClick={onEditClick} type="Add note" />
              </div>
            )}

            {editMode && (
              <Stack gap="sm" orientation="vertical">
                <Textarea
                  maxlength={500}
                  name="note"
                  onChange={onNoteInputChange}
                  testId="notes-input"
                  value={textAreaValue}
                />

                <Stack gap="sm" orientation="horizontal">
                  <ActionIcon onClick={onSaveClick} type="Save" />
                  <ActionIcon onClick={onCancelClick} type="Cancel" />
                </Stack>
              </Stack>
            )}

            {!editMode && sectionData?.notes && (
              <Stack gap="sm" orientation="horizontal">
                <ActionIcon onClick={onEditClick} type="Edit" />
                <ActionIcon onClick={onDeleteClick} type="Delete" />
              </Stack>
            )}
          </Stack>
        </Stack>
      )}
    </div>
  );
};
